import { Injectable, inject } from '@angular/core'
import { CookieService } from 'ngx-cookie-service'

import type { AuthResultDto } from '@/app/domain/dtos/authentication/auth-result.dto'
import type { EmployeeBasicInfoDto } from '@/app/domain/dtos/authentication/employee-basic-info.dto'
import type { MenuDto } from '@/app/domain/dtos/master/menu.dto'

const AUTH_RESULT_KEY = '_REBACK_AUTH_RESULT_'
const EMPLOYEE_KEY = '_REBACK_EMPLOYEE_'
const MENUS_KEY = '_REBACK_MENUS_'

/**
 * Browser-side session persistence only — the actual login/refresh/revoke HTTP calls
 * live in AuthApiService. This keeps the session survivable across a page reload
 * (cookies for authResult/employee), and is what AuthTokenInterceptor and authGuard
 * read synchronously — the NgRx store is rehydrated from this on bootstrap (see
 * authGuard), not the other way around, since in-memory store state doesn't survive
 * a reload. Menus use localStorage, not a cookie: a full-access UserType's granted
 * menu list serializes past the ~4KB per-cookie limit, and the browser silently drops
 * an oversized cookie.set() rather than erroring — that previously meant authGuard's
 * restoreSession always rehydrated menus as [], wiping out what loginSuccess had just
 * set, and the sidebar rendered as if nothing had been granted.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private cookieService = inject(CookieService)

  get authResult(): AuthResultDto | null {
    const raw = this.cookieService.get(AUTH_RESULT_KEY)
    return raw ? JSON.parse(raw) : null
  }

  get accessToken(): string {
    return this.authResult?.accessToken ?? ''
  }

  get refreshToken(): string {
    return this.authResult?.refreshToken ?? ''
  }

  get employee(): EmployeeBasicInfoDto | null {
    const raw = this.cookieService.get(EMPLOYEE_KEY)
    return raw ? JSON.parse(raw) : null
  }

  get menus(): MenuDto[] {
    const raw = localStorage.getItem(MENUS_KEY)
    return raw ? JSON.parse(raw) : []
  }

  saveSession(authResult: AuthResultDto, employee: EmployeeBasicInfoDto, menus: MenuDto[]): void {
    this.cookieService.set(AUTH_RESULT_KEY, JSON.stringify(authResult), undefined, '/')
    this.cookieService.set(EMPLOYEE_KEY, JSON.stringify(employee), undefined, '/')
    localStorage.setItem(MENUS_KEY, JSON.stringify(menus))
  }

  clearSession(): void {
    this.cookieService.delete(AUTH_RESULT_KEY, '/')
    this.cookieService.delete(EMPLOYEE_KEY, '/')
    localStorage.removeItem(MENUS_KEY)
  }
}
